/**
 * Active scope (school unit + academic year) shared by the header switcher,
 * `fetchWithAuth`, and any page that needs the current context.
 *
 * Kept framework-free so `api.ts` can read it without importing React.
 */

export const ALL_SCOPE = 'ALL'

const UNIT_STORAGE_KEY = 'wicend.erp.active_unit'
const ACADEMIC_YEAR_STORAGE_KEY = 'wicend.erp.active_academic_year'

/** Fired whenever unit or academic year changes. */
export const SCOPE_CHANGED_EVENT = 'wicend:scope-changed'
/** Legacy event name, kept so anything already listening keeps working. */
export const UNIT_CHANGED_EVENT = 'wicend:unit-changed'

export interface ActiveScope {
  /** School unit id, or `ALL` for foundation-wide. */
  unitId: string
  /** Academic year id, or `ALL` when none has been picked yet. */
  academicYearId: string
}

function read(key: string): string {
  try {
    return localStorage.getItem(key) ?? ALL_SCOPE
  } catch {
    return ALL_SCOPE
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Private mode / storage disabled: scope stays in memory for this page only.
  }
}

export function getActiveScope(): ActiveScope {
  return {
    unitId: read(UNIT_STORAGE_KEY),
    academicYearId: read(ACADEMIC_YEAR_STORAGE_KEY),
  }
}

/** The unit id to send to the backend, or `null` when no narrowing is requested. */
export function getActiveSchoolUnitId(): string | null {
  const unitId = read(UNIT_STORAGE_KEY)
  return unitId && unitId !== ALL_SCOPE ? unitId : null
}

export function getActiveAcademicYearId(): string | null {
  const yearId = read(ACADEMIC_YEAR_STORAGE_KEY)
  return yearId && yearId !== ALL_SCOPE ? yearId : null
}

export function setActiveScope(patch: Partial<ActiveScope>): void {
  const current = getActiveScope()
  const next: ActiveScope = { ...current, ...patch }
  if (next.unitId === current.unitId && next.academicYearId === current.academicYearId) return

  write(UNIT_STORAGE_KEY, next.unitId)
  write(ACADEMIC_YEAR_STORAGE_KEY, next.academicYearId)

  window.dispatchEvent(new CustomEvent<ActiveScope>(SCOPE_CHANGED_EVENT, { detail: next }))
  if (next.unitId !== current.unitId) {
    window.dispatchEvent(new CustomEvent<string>(UNIT_CHANGED_EVENT, { detail: next.unitId }))
  }
}

/** Stable string for use as a React `key` or effect dependency. */
export function scopeKey(scope: ActiveScope): string {
  return `${scope.unitId}::${scope.academicYearId}`
}

export function subscribeToScope(listener: (scope: ActiveScope) => void): () => void {
  const handler = (event: Event) => {
    listener((event as CustomEvent<ActiveScope>).detail ?? getActiveScope())
  }
  window.addEventListener(SCOPE_CHANGED_EVENT, handler)
  return () => window.removeEventListener(SCOPE_CHANGED_EVENT, handler)
}
